import type { KeyboardEvent } from 'react';
import { useRef } from 'react';

import { TagChip } from '../../atoms/TagChip';
import { cn } from '../../lib/utils';

export interface FilterPillOption {
  value: string;
  label: string;
}

export type FilterPillGroupMode = 'toggle' | 'radiogroup';

export interface FilterPillGroupProps {
  options: ReadonlyArray<FilterPillOption>;
  /** Selected value (single-select). Empty string = none selected. */
  value: string;
  onChange: (next: string) => void;
  /**
   * `'toggle'` (default) — legacy behavior: `role="group"` + `aria-pressed` buttons.
   * `'radiogroup'` — keyboard radiogroup: `role="radiogroup"` + `aria-orientation="horizontal"`
   * + per-pill `role="radio"` + `aria-checked` + ArrowLeft/ArrowRight follow-focus with wrap-around.
   */
  mode?: FilterPillGroupMode;
  /**
   * Accessible name applied to the root container — required for screen-reader
   * users to know what `mode='radiogroup'` controls (WAI-ARIA 1.2 §6.4.5).
   * Recommended on `mode='toggle'` too; harmless when omitted.
   */
  'aria-label'?: string;
  className?: string;
}

/**
 * FilterPillGroup — horizontal row of `TagChip` atoms with single-select
 * behavior per Figma `289:45657` / `289:48604`. The active pill renders in
 * the `pressed` (orange) state; inactive pills render in `default`.
 *
 * Supports two ARIA modes:
 *  - `mode='toggle'` (default) for back-compat with existing callers
 *    (TopDataAccessedBody etc.): root is `role="group"`, each pill exposes
 *    `aria-pressed`.
 *  - `mode='radiogroup'` for WAI-ARIA single-select radiogroup semantics:
 *    root is `role="radiogroup"` + `aria-orientation="horizontal"`, each pill
 *    exposes `role="radio"` + `aria-checked`, and ArrowLeft/ArrowRight
 *    move focus AND select (follow-focus) with wrap-around at the ends.
 */
export function FilterPillGroup({
  options,
  value,
  onChange,
  mode = 'toggle',
  'aria-label': ariaLabel,
  className,
}: FilterPillGroupProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
    if (mode !== 'radiogroup') return;
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const len = options.length;
    if (len === 0) return;
    const dir = e.key === 'ArrowRight' ? 1 : -1;
    const nextIndex = (currentIndex + dir + len) % len; // wrap-around
    const nextOpt = options[nextIndex];
    if (!nextOpt) return;
    onChange(nextOpt.value);
    requestAnimationFrame(() => {
      const next = containerRef.current?.querySelector<HTMLButtonElement>(
        `[data-pill-index="${nextIndex}"]`
      );
      next?.focus();
    });
  };

  const isRadiogroup = mode === 'radiogroup';

  return (
    <div
      ref={containerRef}
      className={cn('flex flex-row flex-wrap items-center gap-2', className)}
      role={isRadiogroup ? 'radiogroup' : 'group'}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
      {...(isRadiogroup ? ({ 'aria-orientation': 'horizontal' } as const) : {})}
    >
      {options.map((opt, idx) => {
        const isActive = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            data-pill-index={idx}
            {...(isRadiogroup
              ? ({ role: 'radio', 'aria-checked': isActive } as const)
              : { 'aria-pressed': isActive })}
            tabIndex={
              isRadiogroup
                ? isActive || (!options.some((o) => o.value === value) && idx === 0)
                  ? 0
                  : -1
                : 0
            }
            onKeyDown={(e) => onKeyDown(e, idx)}
            onClick={() => onChange(opt.value)}
            className="rounded-[4px] outline-none focus-visible:ring-2 focus-visible:ring-dawn-orange"
          >
            <TagChip state={isActive ? 'pressed' : 'default'} weight="regular">
              {opt.label}
            </TagChip>
          </button>
        );
      })}
    </div>
  );
}

FilterPillGroup.displayName = 'FilterPillGroup';
